/**
 * Y-axis range control for timeseries charts: an AUTO / CUSTOM toggle and,
 * when CUSTOM, a min/max pair. The parent owns the state and derives the
 * recharts `domain` from it (CUSTOM + valid numbers → [min, max]; else
 * ['auto','auto']). See `parseYDomain` below.
 */
export interface YAxisRangeState {
  mode: 'auto' | 'custom';
  min: string;
  max: string;
}

export const EMPTY_Y_RANGE: YAxisRangeState = { mode: 'auto', min: '', max: '' };

/** Derive the recharts <YAxis domain> + whether to allowDataOverflow. */
export function parseYDomain(s: YAxisRangeState): {
  domain: [number | string, number | string];
  clamp: boolean;
} {
  const lo = parseFloat(s.min);
  const hi = parseFloat(s.max);
  const valid = s.mode === 'custom' && Number.isFinite(lo) && Number.isFinite(hi) && lo < hi;
  return valid ? { domain: [lo, hi], clamp: true } : { domain: ['auto', 'auto'], clamp: false };
}

export function YAxisRangeControl({
  state,
  onChange,
  label = 'Y-AXIS',
}: {
  state: YAxisRangeState;
  onChange: (next: YAxisRangeState) => void;
  label?: string;
}) {
  const lo = parseFloat(state.min);
  const hi = parseFloat(state.max);
  const minBad = !!state.min && !Number.isFinite(lo);
  const maxBad = !!state.max && (!Number.isFinite(hi) || (Number.isFinite(lo) && hi <= lo));
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-mono-500 tracking-wider">{label}:</span>
      {(['auto', 'custom'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange({ ...state, mode: m })}
          className={`px-2 py-1 text-[10px] tracking-wider rounded transition-colors ${
            state.mode === m ? 'bg-mono-700 text-mono-100' : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
          }`}
        >
          {m.toUpperCase()}
        </button>
      ))}
      {state.mode === 'custom' && (
        <div className="flex items-center gap-1">
          <input
            type="text" inputMode="decimal" placeholder="min"
            className={`w-16 bg-mono-950 border rounded px-1.5 py-0.5 text-[10px] text-mono-100 focus:outline-none ${
              minBad ? 'border-red-600' : 'border-mono-700 focus:border-cyan-600'
            }`}
            value={state.min}
            onChange={(e) => onChange({ ...state, min: e.target.value })}
          />
          <span className="text-mono-600 text-[10px]">–</span>
          <input
            type="text" inputMode="decimal" placeholder="max"
            className={`w-16 bg-mono-950 border rounded px-1.5 py-0.5 text-[10px] text-mono-100 focus:outline-none ${
              maxBad ? 'border-red-600' : 'border-mono-700 focus:border-cyan-600'
            }`}
            value={state.max}
            onChange={(e) => onChange({ ...state, max: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
